import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { AuthProvider } from "@/hooks/useAuth";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminProtectedRoute from "@/components/AdminProtectedRoute";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { AppLayout } from "@/components/layout";
import DocsSkeleton from "./pages/DocsSkeleton";
import Dashboard from "./pages/Dashboard";
import DomainsPage from "./pages/DomainsPage";
import CloudflarePage from "./pages/CloudflarePage";
import DnsChangePage from "./pages/DnsChangePage";
import HostingsPage from "./pages/HostingsPage";
import SiteMapPage from "./pages/SiteMapPage";
import ThreadsPage from "./pages/ThreadsPage";
import TasksPage from "./pages/TasksPage";
import SettingsPage from "./pages/SettingsPage";
import ThematicMapsPage from "./pages/ThematicMapsPage";
import ThematicMapDetailPage from "./pages/ThematicMapDetailPage";
import AutoGenerateMapsPage from "./pages/AutoGenerateMapsPage";
import { BulkAutoGenerateProvider } from "./contexts/BulkAutoGenerateContext";
import MapVisualPage from "./pages/MapVisualPage";
import StaticSitesPage from "./pages/StaticSitesPage";
import StaticSiteStudioPage from "./pages/StaticSiteStudioPage";
import ApiCostsPage from "./pages/ApiCostsPage";
import Auth from "./pages/Auth";
import Setup2FA from "./pages/Setup2FA";
import { TwoFactorGate } from "@/components/TwoFactorGate";
import NotFound from "./pages/NotFound";

// Standalone Mode (Artykuły) — lazy load gated by feature flag
const StandaloneArticlesPage = lazy(() => import("./pages/StandaloneArticlesPage"));
const STANDALONE_ENABLED = import.meta.env.VITE_STANDALONE_MODE_ENABLED === "true";

// Lazy: bundle viewer ładuje się PO authoryzacji (AdminProtectedRoute gate).
const DocsPage = lazy(() => import("./pages/DocsPage"));

const queryClient = new QueryClient();

const App = () => (
  <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BulkAutoGenerateProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
          <Routes>
            <Route path="/auth" element={<Auth />} />

            {/* Obowiązkowa konfiguracja 2FA — wymaga logowania, ale CELOWO poza
                TwoFactorGate (inaczej pętla redirect: gate → /setup-2fa → gate). */}
            <Route path="/setup-2fa" element={<ProtectedRoute><Setup2FA /></ProtectedRoute>} />

            <Route element={<ProtectedRoute><TwoFactorGate><AppLayout /></TwoFactorGate></ProtectedRoute>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/domains" element={<DomainsPage />} />
              <Route path="/cloudflare" element={<CloudflarePage />} />
              <Route path="/dns" element={<DnsChangePage />} />
              <Route path="/hostings" element={<HostingsPage />} />
              <Route path="/sitemap" element={<SiteMapPage />} />
              <Route path="/pomysly" element={<ThreadsPage />} />
              <Route path="/taski" element={<TasksPage />} />
              <Route path="/mapy-tematyczne" element={<ThematicMapsPage />} />
              <Route path="/mapy-tematyczne/auto-generate" element={<AutoGenerateMapsPage />} />
              <Route path="/mapy-tematyczne/:id" element={<ThematicMapDetailPage />} />
              {STANDALONE_ENABLED && (
                <Route
                  path="/artykuly"
                  element={
                    <Suspense fallback={<div className="p-6">Ładowanie...</div>}>
                      <StandaloneArticlesPage />
                    </Suspense>
                  }
                />
              )}
              <Route path="/mapa-wizualna" element={<MapVisualPage />} />
              <Route path="/strony" element={<StaticSitesPage />} />
              <Route path="/strony/:websiteId" element={<StaticSiteStudioPage />} />
              <Route path="/koszty-api" element={<ApiCostsPage />} />
              <Route path="/ustawienia" element={<SettingsPage />} />
              <Route
                path="/docs/*"
                element={
                  <AdminProtectedRoute>
                    <Suspense fallback={<DocsSkeleton />}>
                      <DocsPage />
                    </Suspense>
                  </AdminProtectedRoute>
                }
              />
            </Route>

            {/* Przekierowanie starego URL */}
            <Route path="/tablica" element={<Navigate to="/pomysly" replace />} />

            {/* Przekierowania starych URL */}
            <Route path="/backend-management" element={<Navigate to="/domains" replace />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          {/* AppLogFab przeniesiony do AppLayout (2026-07-22) — tutaj renderował
              się na KAŻDEJ trasie, także na /auth przed zalogowaniem. */}
          </BrowserRouter>
        </TooltipProvider>
        </BulkAutoGenerateProvider>
      </AuthProvider>
    </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
